/**
 * A short chime for campaign terminal states.
 *
 * A long campaign often finishes while the operator is on another tab, where a
 * silent panel is easy to miss until the session has timed out. Two soft notes
 * are enough to notice without being disruptive, and their direction says which
 * kind of ending it was: rising for a clean completion, falling for a stop, an
 * error, or a cancellation.
 *
 * Synthesised through WebAudio so there is no asset to ship or cache. Every
 * failure path is silent: audio is a convenience, never a precondition for
 * reading the panel, so a blocked autoplay policy, a missing AudioContext, or a
 * headless test environment must not surface an error.
 *
 * @module ui/dialog/WorkflowPane/campaignChime
 */

const NOTE_SECONDS = 0.16;
const NOTE_GAP_SECONDS = 0.12;
// Well below a notification sound; audible in a quiet room, easy to talk over.
const PEAK_GAIN = 0.06;

const TONES = Object.freeze({
    complete: [660, 880],
    stopped: [520, 390],
});

let sharedContext = null;

function audioContext() {
    if (typeof window === 'undefined') return null;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    if (!sharedContext) sharedContext = new Ctor();
    return sharedContext;
}

function playNote(context, frequency, startAt) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;
    // Ramp both edges: an instant start or stop on a sine wave clicks.
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, startAt + 0.02);
    gain.gain.linearRampToValueAtTime(0, startAt + NOTE_SECONDS);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + NOTE_SECONDS + 0.02);
}

/**
 * Play the chime for a campaign that has just reached a terminal state.
 *
 * @param {'complete'|'stopped'} kind - which ending to signal.
 * @returns {boolean} whether audio was actually scheduled.
 */
export function playCampaignChime(kind = 'complete') {
    try {
        const context = audioContext();
        if (!context) return false;
        // Starting a campaign is itself a user gesture, so a context suspended by
        // the autoplay policy can be resumed here. Ignore the rejection if not.
        if (context.state === 'suspended' && typeof context.resume === 'function') {
            const resumed = context.resume();
            if (resumed && typeof resumed.catch === 'function') resumed.catch(() => {});
        }
        const tones = TONES[kind] || TONES.complete;
        tones.forEach((frequency, index) => {
            playNote(context, frequency, context.currentTime + index * NOTE_GAP_SECONDS);
        });
        return true;
    } catch (err) {
        return false;
    }
}

/**
 * Which chime a terminal panel summary should get, or null when the summary is
 * not terminal. Keeps the sound decision in one place with the label mapping it
 * mirrors, rather than re-deriving terminal status in the component.
 *
 * @param {{active?: boolean, label?: string, status?: string}} summary
 * @returns {'complete'|'stopped'|null}
 */
export function chimeKindForSummary(summary) {
    if (!summary || summary.active !== false) return null;
    if (summary.label === 'Complete') return 'complete';
    if (summary.label === 'Stopped' || summary.label === 'Cancelled' || summary.label === 'Error') {
        return 'stopped';
    }
    return null;
}
